import React, { lazy, Suspense, useMemo } from "react";
import { CssBaseline, ThemeProvider } from "@mui/material";
import { ConfigProvider } from 'antd';
import trTR from 'antd/locale/tr_TR';
import dayjs from 'dayjs';
import 'dayjs/locale/tr';

dayjs.locale('tr');
import { createTheme } from "@mui/material/styles";
import { useSelector } from "react-redux";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { themeSettings } from "./theme";
import { FONT_STACK } from "./theme/modernTheme";
import Layout from "./scenes/layout";
import Login from "./components/Login";
import Register from "./components/Register";
import './styles/forms.css';

const Dashboard = lazy(() => import("./scenes/dashboard"));
const LiveOps = lazy(() => import("./scenes/liveOps"));
const Products = lazy(() => import("./scenes/products"));
const Customers = lazy(() => import("./scenes/customers"));
const Geography = lazy(() => import("./scenes/geography"));
const Overview = lazy(() => import("./scenes/overview"));
const Daily = lazy(() => import("./scenes/daily"));
const Monthly = lazy(() => import("./scenes/monthly"));
const Breakdown = lazy(() => import("./scenes/breakdown"));
const Admin = lazy(() => import("./scenes/admin"));
const Performance = lazy(() => import("./scenes/performance"));
const Orders = lazy(() => import("./scenes/orders"));
const AbandonedCarts = lazy(() => import("./scenes/abandonedCarts"));
const ExaminedProducts = lazy(() => import("./scenes/examinedProducts"));
const TechnicalBlog = lazy(() => import("./scenes/technicalBlog"));
const ManualOrder = lazy(() => import("./components/ManualOrder"));
const OrderDetails = lazy(() => import("./components/OrderDetails"));
const TedarikciYonetimi = lazy(() => import("./components/TedarikciYonetimi"));
const Projeler = lazy(() => import("./components/Projeler"));
const ProjeEkle = lazy(() => import("./components/ProjeEkle"));
const ProjeDuzenle = lazy(() => import("./components/ProjeDuzenle"));
const ProfilAyarlari = lazy(() => import("./components/ProfilAyarlari"));
const TestComponent = lazy(() => import("./components/TestComponent"));
const CinSeyahatiNotlari = lazy(() => import("./components/CinSeyahatiNotlari"));
const CinToplantilar = lazy(() => import("./components/CinToplantilar"));
const PriceListFormulation = lazy(() => import("./components/PriceListFormulation"));
const FiyatGuncelleme = lazy(() => import("./scenes/fiyatGuncelleme"));
const Gorevler = lazy(() => import("./components/Gorevler"));
const ProductIdeas = lazy(() => import("./components/ProductIdeas"));
const CurrencyConverter = lazy(() => import("./scenes/finans/CurrencyConverter"));
const PrimHesaplama = lazy(() => import("./scenes/finans/PrimHesaplama"));
const KarZararHesabi = lazy(() => import("./scenes/finans/KarZararHesabi"));
const HepsiburadaSiparisler = lazy(() => import("./scenes/finans/HepsiburadaSiparisler"));
const OfisStok = lazy(() => import("./scenes/ofisStok"));
const OfisStogaCekilecek = lazy(() => import("./scenes/ofisStogaCekilecek"));
const HbRpiListings = lazy(() => import("./scenes/hbRpi"));
const IliskiPipeline = lazy(() => import("./scenes/iliskiPipeline"));
const ProjeArsivi = lazy(() => import("./scenes/projeArsivi"));
const SeoReadiness = lazy(() => import("./scenes/seoReadiness"));
const Proformalar = lazy(() => import("./scenes/proformalar"));
const Teklifler = lazy(() => import("./scenes/teklifler"));
const Faturalar = lazy(() => import("./scenes/faturalar"));
const DatasheetGenerator = lazy(() => import("./scenes/datasheetGenerator"));
const ProductSkuCatalog = lazy(() => import("./scenes/productSkuCatalog"));
const SatinAlmaFormu = lazy(() => import("./scenes/satinAlmaFormu"));
const LandedCostDefteri = lazy(() => import("./scenes/landedCost"));
const HepsiburadaPricing = lazy(() => import("./components/HepsiburadaPricing"));
const USPriceComparison = lazy(() => import("./components/USPriceComparison"));
const NotFoundPage = lazy(() => import("./components/NotFoundPage"));

function RouteFallback() {
  return (
    <div
      style={{
        minHeight: "42vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#64748b",
        fontSize: 14,
      }}
    >
      Yükleniyor…
    </div>
  );
}

/** Rota değişince token tekrar okunur — giriş sonrası /gorevler vb. herkes için açılır */
function AppRoutes() {
  const location = useLocation();
  const mode = useSelector((state) => state.global.mode);
  const theme = useMemo(() => createTheme(themeSettings(mode)), [mode]);
  const isAuthenticated = useMemo(
    () => !!localStorage.getItem('token'),
    [location.pathname, location.key]
  );

  const antdTheme = {
    token: {
      colorPrimary: mode === 'dark' ? '#818cf8' : '#0a0a0a',
      colorSuccess: '#22c55e',
      colorWarning: '#eab308',
      colorError: '#ef4444',
      colorInfo: mode === 'dark' ? '#737373' : '#525252',
      colorBgContainer: mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#ffffff',
      colorBgElevated: mode === 'dark' ? '#1e293b' : '#ffffff',
      colorBgLayout: mode === 'dark' ? '#0f172a' : '#f8fafc',
      colorBorder: mode === 'dark' ? 'rgba(255,255,255,0.14)' : 'rgba(10,10,10,0.12)',
      colorText: mode === 'dark' ? '#f1f5f9' : '#0a0a0a',
      colorTextSecondary: mode === 'dark' ? '#94a3b8' : '#64748b',
      colorTextPlaceholder: mode === 'dark' ? '#64748b' : '#94a3b8',
      borderRadius: 8,
      fontSize: 13,
      controlHeight: 40,
      fontFamily: FONT_STACK,
      zIndexPopupBase: 1500,
    },
    components: {
      Drawer: {
        colorBgElevated: mode === 'dark' ? '#1e293b' : '#ffffff',
        colorText: mode === 'dark' ? '#f1f5f9' : '#0f172a',
      },
      Modal: {
        contentBg: mode === 'dark' ? '#1e293b' : '#ffffff',
        headerBg: mode === 'dark' ? '#1e293b' : '#ffffff',
      },
    },
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ConfigProvider
        locale={trTR}
        theme={antdTheme}
        modal={{ maskClosable: false, keyboard: false, zIndex: 1500 }}
        drawer={{ maskClosable: false, keyboard: true, zIndex: 1500 }}
      >
        <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route element={isAuthenticated ? <Layout /> : <Navigate to="/login" replace state={{ from: location }} />}>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/live-ops" element={<LiveOps />} />
            <Route path="/products" element={<Products />} />
            <Route path="/populer-urunler" element={<ExaminedProducts />} />
            <Route path="/teknik-blog" element={<TechnicalBlog />} />
            <Route path="/incelenen-urunler" element={<Navigate to="/populer-urunler" replace />} />
            <Route path="/customers" element={<Customers />} />
            <Route path="/tedarikci" element={<TedarikciYonetimi />} />
            <Route path="/satin-alma-formu" element={<SatinAlmaFormu />} />
            <Route path="/landed-cost" element={<LandedCostDefteri />} />
            <Route path="/geography" element={<Geography />} />
            <Route path="/overview" element={<Overview />} />
            <Route path="/daily" element={<Daily />} />
            <Route path="/monthly" element={<Monthly />} />
            <Route path="/breakdown" element={<Breakdown />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/performance" element={<Performance />} />
            <Route path="/orders" element={<Orders />} />
            <Route path="/terk-edilen-sepetler" element={<AbandonedCarts />} />
            <Route path="/manual-order" element={<ManualOrder />} />
            <Route path="/order-details/:id" element={<OrderDetails />} />
            <Route path="/shopify-test" element={<Navigate to="/projeler" replace />} />
            <Route path="/gorevler" element={<Gorevler />} />
            <Route path="/projeler" element={<Projeler />} />
            <Route path="/proje-ekle" element={<ProjeEkle />} />
            <Route path="/proje-duzenle/:id" element={<ProjeDuzenle />} />
            <Route path="/test-proje/:id" element={<ProjeDuzenle />} />
            <Route path="/profil" element={<ProfilAyarlari />} />
            <Route path="/test-component" element={<TestComponent />} />
            <Route path="/cin-seyahati" element={<CinSeyahatiNotlari />} />
            <Route path="/cin-toplantilar" element={<CinToplantilar />} />
            <Route path="/fiyat-listesi" element={<PriceListFormulation />} />
            <Route path="/fiyat-guncelleme" element={<FiyatGuncelleme />} />
            <Route path="/abd-fiyat-karsilastirma" element={<USPriceComparison />} />
            <Route path="/hepsiburada-fiyat" element={<HepsiburadaPricing />} />
            <Route path="/friendlyelec-fiyat" element={<Navigate to="/fiyat-guncelleme" replace />} />
            <Route path="/urun-fikirleri" element={<ProductIdeas />} />
            <Route path="/ofis-stok" element={<OfisStok />} />
            <Route path="/ofis-stoga-cekilecek" element={<OfisStogaCekilecek />} />
            <Route path="/hb-rpi" element={<HbRpiListings />} />
            <Route path="/iliski-pipeline" element={<IliskiPipeline />} />
            <Route path="/proje-arsivi" element={<ProjeArsivi />} />
            <Route path="/seo-readiness" element={<SeoReadiness />} />
            <Route path="/finans/kur" element={<CurrencyConverter />} />
            <Route path="/finans/prim" element={<PrimHesaplama />} />
            <Route path="/finans/kar-zarar" element={<KarZararHesabi />} />
            <Route path="/finans/hepsiburada" element={<HepsiburadaSiparisler />} />
            <Route path="/proformalar" element={<Proformalar />} />
            <Route path="/teklifler" element={<Teklifler />} />
            <Route path="/transactions" element={<Faturalar />} />
            <Route path="/datasheet-generator" element={<DatasheetGenerator />} />
            <Route path="/urun-sku-datasheet" element={<ProductSkuCatalog />} />

            <Route path="/gumruk-detaylari" element={<Navigate to="/landed-cost" replace />} />
            <Route path="/iletisim/mail" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dokumanlar" element={<Navigate to="/dashboard" replace />} />
            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Routes>
        </Suspense>
      </ConfigProvider>
    </ThemeProvider>
  );
}

function App() {
  return (
    <div className="app">
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </div>
  );
}

export default App;
